import React, { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { Mail, Phone, MapPin, Send, ChevronRight, Sparkles } from "lucide-react";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        // Template params matching EmailJS template variables
        const templateParams = {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone,
            subject: formData.subject,
            message: formData.message,
            to_name: "Adsurg Team",
        };

        const backendData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            subject: formData.subject,
            message: formData.message,
            type: "CONTACT",
        };

        try {
            // Modern EmailJS syntax with publicKey as option
            const response = await emailjs.send(
                "service_1hmotcr",
                "template_t4zvfjk",
                templateParams,
                {
                    publicKey: "VWAvmxQ2rX99OLBpi",
                }
            );

            console.log("✅ SUCCESS!", response.status, response.text);

            // Save to Backend
            try {
                // Ensure to import axios correctly at the top, if not I am using fetch here to avoid breaking imports
                const fetchRes = await fetch(process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/contacts` : "http://localhost:5000/api/contacts", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(backendData),
                });

                if (fetchRes.ok) {
                    console.log("✅ Backend Save SUCCESS");
                } else {
                    console.error("❌ Backend Save Error:", await fetchRes.text());
                }
            } catch (backendErr) {
                console.error("❌ Backend Request Error:", backendErr);
            }

            setSubmitted(true);
            setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
            setTimeout(() => setSubmitted(false), 5000);
        } catch (err) {
            console.error("❌ EmailJS Error:", err);

            // Better error handling
            let errorMessage = "Failed to send message. Please try again.";
            if (err.status === 400) {
                errorMessage = "Invalid request. Please check your input.";
            } else if (err.status === 412) {
                errorMessage = "Template configuration error.";
            } else if (err.text) {
                errorMessage = err.text;
            }

            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">

            {/* ─── Hero Banner ─── */}
            <div className="relative bg-gradient-to-br from-[#00809D] via-[#006d87] to-[#005568] overflow-hidden">
                {/* Decorative shapes */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-white/[0.02] rounded-full" />

                <div className="relative z-10 pt-32 sm:pt-36 lg:pt-40 pb-20 sm:pb-24 lg:pb-28 px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 25 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
                            <Sparkles className="w-4 h-4 text-white/90" />
                            <span className="text-white/90 font-medium text-sm">We'd love to hear from you</span>
                        </div>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-5 leading-tight tracking-tight">
                            Contact Us
                        </h1>
                        <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                            Ready to grow your business? Get in touch and let's discuss how we can help you achieve your advertising goals.
                        </p>
                    </motion.div>
                </div>

                {/* Curved bottom edge */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="white" />
                    </svg>
                </div>
            </div>



            {/* ─── Main Content ─── */}
            <div className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

                        {/* ─── Left: Form ─── */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, delay: 0.3 }}
                        >
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                Send us a Message
                            </h2>
                            <p className="text-gray-500 mb-8 text-sm sm:text-base">
                                Fill out the form and our team will respond within 24 hours.
                            </p>

                            {submitted && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3"
                                >
                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <p className="text-emerald-700 font-medium text-sm">Message sent successfully! We'll get back to you soon.</p>
                                </motion.div>
                            )}

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3"
                                >
                                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                    <p className="text-red-700 font-medium text-sm">{error}</p>
                                </motion.div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                                            Full Name <span className="text-[#00809D]">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            placeholder="John Doe"
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/15 focus:bg-white transition-all duration-300 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                                            Email <span className="text-[#00809D]">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            placeholder="john@example.com"
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/15 focus:bg-white transition-all duration-300 text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-gray-700 text-sm font-semibold mb-2">Phone</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="+91 98765 43210"
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/15 focus:bg-white transition-all duration-300 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                                            Subject <span className="text-[#00809D]">*</span>
                                        </label>
                                        <select
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-gray-800 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/15 focus:bg-white transition-all duration-300 text-sm appearance-none cursor-pointer"
                                        >
                                            <option value="" className="text-gray-400">Select a subject</option>
                                            <option value="General Inquiry">General Inquiry</option>
                                            <option value="Campaign Management">Campaign Management</option>
                                            <option value="Pricing & Plans">Pricing & Plans</option>
                                            <option value="Technical Support">Technical Support</option>
                                            <option value="Partnership">Partnership</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">
                                        Message <span className="text-[#00809D]">*</span>
                                    </label>
                                    <textarea
                                        name="message"
                                        rows="5"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        placeholder="Tell us about your project or how we can help..."
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/15 focus:bg-white transition-all duration-300 text-sm resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="group w-full sm:w-auto bg-[#00809D] hover:bg-[#006d87] text-white px-8 py-4 rounded-xl font-semibold text-sm shadow-lg shadow-[#00809D]/25 hover:shadow-xl hover:shadow-[#00809D]/35 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Send Message
                                            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </motion.div>

                        {/* ─── Right: Info Cards ─── */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, delay: 0.4 }}
                            className="space-y-6"
                        >
                            {/* FAQ Teaser */}
                            <div className="bg-gradient-to-br from-[#00809D] to-[#006d87] rounded-2xl p-7 sm:p-8 text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                                <div className="relative">
                                    <h3 className="text-xl sm:text-2xl font-bold mb-3">Start Your Campaign Today</h3>
                                    <p className="text-white/80 text-sm leading-relaxed mb-6">
                                        Join 500+ businesses that have transformed their digital advertising with our expert team. Free consultation included.
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        <div className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium border border-white/10">
                                            ✓ Free Audit
                                        </div>
                                        <div className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium border border-white/10">
                                            ✓ No Lock-in
                                        </div>
                                        <div className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium border border-white/10">
                                            ✓ 24/7 Support
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Services Quick List */}
                            <div className="bg-gray-50 rounded-2xl p-7 sm:p-8 border border-gray-100">
                                <h3 className="text-lg font-bold text-gray-900 mb-5">Our Services</h3>
                                <div className="space-y-3.5">
                                    {[
                                        "Social Media Advertising",
                                        "Google & Search Ads",
                                        "Video & YouTube Campaigns",
                                        "E-commerce Ad Management",
                                        "Performance Marketing",
                                        "Brand Strategy & Creative",
                                    ].map((service, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-3 group cursor-default"
                                        >
                                            <div className="w-7 h-7 rounded-lg bg-[#00809D]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#00809D]/20 transition-colors">
                                                <ChevronRight className="w-3.5 h-3.5 text-[#00809D]" />
                                            </div>
                                            <span className="text-gray-600 text-sm font-medium group-hover:text-gray-900 transition-colors">
                                                {service}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Working Hours */}
                            <div className="bg-gray-50 rounded-2xl p-7 sm:p-8 border border-gray-100">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Working Hours</h3>
                                <div className="space-y-3">
                                    {[
                                        { day: "Monday – Saturday", time: "9:00 AM – 7:30 PM" },
                                        { day: "Sunday", time: "Closed" },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                            <span className="text-gray-600 text-sm">{item.day}</span>
                                            <span className={`text-sm font-semibold ${item.time === "Closed" ? "text-red-400" : "text-[#00809D]"}`}>
                                                {item.time}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
